import { TableContextActions, TableContextValue, TableFilter } from "./types";

export const INITIAL_CONTEXT_DISPATCH = {
    setItems: () => {},
    addFilter: () => {},
    removeFilter: () => {},
    setSearchValue: () => {},
    clearSearchValue: () => {},
    removeAllFilters: () => {},
    selectItem: () => {},
    editItems: () => {},
    deleteItems: () => {},
    refreshItems: () => Promise.resolve(),
    setPage: () => {},
    setPerPage: () => {},
    addHiddenColumn: () => {},
    removeHiddenColumn: () => {},
};

export const INITIAL_CONTEXT_STATE = {
    tableData: [] as any,
    error: null,
    loading: true, // Set to true to trigger initial fetch
    filters: [] as any[],
    searchValue: "",
    disabledRows: [],
    selectedItems: [],
    perPage: 25,
    page: 1,
    total: 0,
    hiddenColumns: [] as string[],
};

export const ID_KEY = "id";
export const HIDDEN_COLUMNS_KEY = "hiddenColumns";
export const USER_HIDDEN_COLUMNS_KEY = "userHiddenColumns";
export const USER_DEFAULT_HIDDEN_COLUMNS = ["wp_user_id", "user_login"];
export const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Base class with common table state handling logic
export class BaseTableStateHandler<TData, TFilter extends TableFilter<any>> {
    state: TableContextValue<TData, TFilter>;
    
    constructor(state: TableContextValue<TData, TFilter>) {
        this.state = state;
    }

    [TableContextActions.SET_ITEMS]({
        items,
        total
    }: {
        items: TData;
        total: number;
    }) {
        const newItems = new Set(
            (items as any)?.map((item: any) => item[ID_KEY]) || []
        );
        this.setState({
            tableData: items,
            loading: false,
            error: null,
            disabledRows: [],
            selectedItems: this.state.selectedItems?.filter((id) =>
                newItems.has(id)
            ),
            total
        });
    }

    [TableContextActions.ADD_FILTER](filter: TFilter) {
        const newFilters = [...this.state.filters, filter];

        const filterExists = this.state.filters.some(
            (f) => 
            f.column === filter.column &&
            f.type === filter.type &&
            f.value === filter.value
        );
        if (filterExists) return;
        this.state = {
            ...this.state,
            loading: true,
            filters: newFilters,
            page: 1,
        };
    }

    [TableContextActions.REMOVE_FILTER](filterToRemove: TFilter | TFilter[]) {
        if (Array.isArray(filterToRemove)) { 
            const newFilters = this.state.filters.filter(
                (filter) => !filterToRemove.find((f) => f.id === filter.id)
            );

            this.state = {
            ...this.state,
            loading: true,
            filters: newFilters,
            page: 1,
            };
        } else {
            const newFilters = this.state.filters.filter(
                (filter) => filter.id !== filterToRemove.id
            );

            this.state = {
                ...this.state,
                loading: true,
                filters: newFilters,
                page: 1,
            };
        }
    }

    [TableContextActions.REMOVE_ALL_FILTERS]() {
        this.state = {
            ...this.state,
            loading: true,
            filters: [],
            page: 1,
        };
    }

    [TableContextActions.SET_SEARCH_VALUE](searchValue: string) {
        if (searchValue === this.state.searchValue) return;
        this.state = {
            ...this.state,
            loading: true,
            searchValue,
            page: 1,
        };
    }

    [TableContextActions.SET_ERROR_MESSAGE](errorMessage: string){
        this.state = {
            ...this.state, 
            error: errorMessage
        }
    }

    [TableContextActions.SET_DISABLED_ROWS](disabledRows: (number | string)[]) {
        this.state = {
            ...this.state,
            disabledRows: disabledRows,
        };
    }

    [TableContextActions.CLEAR_DISABLED_ROWS]() {
        this.state = {
            ...this.state,
            disabledRows: [],
        };
    }

    [TableContextActions.SET_SELECTED_ITEMS](selectedItems: (number | string)[]) {
        this.state = {
            ...this.state,
            selectedItems,
        };
    }

    [TableContextActions.SELECT_ITEM](itemId: number | string | number[] | string[]) {
        if (typeof itemId === "number" || typeof itemId === "string") {
            const selectedItems = this.state.selectedItems;
            this.setState({
                selectedItems: selectedItems.includes(itemId)
                    ? selectedItems.filter((id) => id !== itemId)
                    : [...selectedItems, itemId]
            })
        } else if (Array.isArray(itemId)) {
            const isRemoveAll = itemId.length === this.state.selectedItems.length;
            this.setState({
                selectedItems: isRemoveAll ? [] : itemId
            });
        }
    }

    [TableContextActions.CLEAR_SELECTED_ITEMS]() {
        this.setState({
            selectedItems: []
        });
    }

    [TableContextActions.EDIT_SELECTED_ITEMS]({
        update, 
        items,
    } : { update: any; items: any }) {
        this[TableContextActions.SET_DISABLED_ROWS](this.state.selectedItems);
        this.state = {
            ...this.state,
            loading: true
        }
    }
    [TableContextActions.DELETE_SELECTED_ITEMS]() {
        this.state = {
            ...this.state,
            loading: true,
        };
    }

    [TableContextActions.SET_PAGE](page: number) {
        if (page === this.state.page) return;
        this.state = {
            ...this.state,
            loading: true,
            page,
        };
    }

    [TableContextActions.SET_PER_PAGE](perPage: number) {
        if (perPage === this.state.perPage) return;
        this.state = {
            ...this.state,
            loading: true,
            perPage,
            page: 1
        };
    }

    [TableContextActions.SET_HIDDEN_COLUMNS](hiddenColumns: string[]) {
        this.state = {
            ...this.state,
            hiddenColumns,
        };
    }

    setState(update: Partial<TableContextValue<TData, TFilter>>) {
        this.state = { 
            ...this.state, 
            ...update 
        };
    }

    getState(){
        return { ...this.state };
    }
}

export const buildFetchParams = <TFilter, TModel>(
  filters: TFilter[],
  perPage: number,
  page: number
) => {
  const params = [
    ...filters,
    {
      id: 2001,
      type: "is",
      column: "per_page" as unknown as keyof TModel,
      value: perPage,
    } as TFilter,
    {
      id: 2002,
      column: "page" as unknown as keyof TModel,
      type: "is",
      value: page,
    } as TFilter,
  ];
  return params;
};